import React, { useMemo } from "react"
import { Button, ButtonBase, Theme, Tooltip, Typography, makeStyles } from "@material-ui/core"
import { Check } from "react-feather"
import { useTranslation } from "react-i18next"

import { StorageFileViewModel } from "../../storage/api/storage-file"
import { MessageViewModel } from "../api/message"
import { MessageReaction } from "../api/message-response"
import { isEmojiOnlyMessage } from "../utils/emojis"
import MentionedContent from "../utils/mentions"
import ChatAttachment, { canPreviewVideo } from "./ChatAttachment"
import MessageEditor from "./MessageEditor"
import MessageActionsMenu from "./MessageActionsMenu"

type Props = {
  message: MessageViewModel
  currentUserId?: string
  editing?: boolean
  savingEdit?: boolean
  highlighted?: boolean
  onReply: () => void
  onJumpToMessage: (messageId: string) => void
  onToggleReaction: (emoji: string, reacted: boolean) => void
  onAddReaction: (anchorEl: HTMLElement) => void
  onTogglePin: () => void
  onSetDeleted: (deleted: boolean) => void
  onStartEdit: () => void
  onCancelEdit: () => void
  onSubmitEdit: (content: string) => void
  // The sender is no longer a participant of this chat.
  senderLeft?: boolean
  // Names of everyone (other than you) who has read this message. An entry is undefined when the
  // reader's name couldn't be resolved — they're no longer a member of the org.
  readBy?: (string | undefined)[]
}

type ReactionGroup = {
  emoji: string
  names: string[]
  mine: boolean
}

const isMediaAttachment = (contentType: string) => {
  if (contentType.startsWith("image/")) return true
  if (contentType.startsWith("video/")) return canPreviewVideo(contentType)
  return false
}

const groupReactions = (reactions: MessageReaction[] = [], currentUserId?: string): ReactionGroup[] => {
  const groups = new Map<string, ReactionGroup>()

  reactions.forEach(({ emoji, userId, userName }) => {
    const group = groups.get(emoji) ?? { emoji, names: [], mine: false }

    group.names.push(userName)
    group.mine = group.mine || userId === currentUserId

    groups.set(emoji, group)
  })

  return Array.from(groups.values())
}

const ChatMessage = ({
  message,
  currentUserId,
  editing,
  savingEdit,
  highlighted,
  onReply,
  onJumpToMessage,
  onToggleReaction,
  onAddReaction,
  onTogglePin,
  onSetDeleted,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,
  readBy = [],
  senderLeft,
}: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")
  const { t: common } = useTranslation("translation")

  const { id, content, senderName, senderId, attachments, replyToMessage: repliedTo } = message
  const mine = senderId === currentUserId
  const deleted = Boolean(message.deletedAt)
  const pinned = Boolean(message.pinnedAt)
  // A message of your own that the system didn't write is yours to edit or delete.
  const canModify = mine && message.type !== "system"
  // Only your own messages carry a receipt — you already know what you've read.
  const showReceipt = mine && message.type !== "system" && !deleted
  const readByLabel = readBy.map((name) => name || t("chatMessage.unknownReaderLabel")).join(", ")

  const emojiOnly = useMemo(() => isEmojiOnlyMessage(content), [content])

  const reactionGroups = useMemo(
    () => groupReactions(message.reactions, currentUserId),
    [message.reactions, currentUserId]
  )

  const createdAt = new Date(message.createdAt)
  const time = createdAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })

  const { mediaFiles, otherFiles } = useMemo(() => {
    const mediaFiles: StorageFileViewModel[] = []
    const otherFiles: StorageFileViewModel[] = []

    attachments?.forEach((file) => {
      if (isMediaAttachment(file.contentType)) {
        mediaFiles.push(file)
      } else {
        otherFiles.push(file)
      }
    })

    return { mediaFiles, otherFiles }
  }, [attachments])

  return (
    <div data-message-id={id} className={`${classes.row} ${mine ? classes.rowMine : classes.rowOther}`}>
      <div className={`${classes.stack} ${mine ? classes.stackMine : ""}`}>
        {deleted ? (
          <div className={`${classes.container} ${classes.deleted}`}>
            <Typography className={classes.deletedText}>{t("chatMessage.deletedMessage")}</Typography>

            {mine && (
              <Button size="small" className={classes.undo} onClick={() => onSetDeleted(false)}>
                {t("chatMessage.undoButtonLabel")}
              </Button>
            )}
          </div>
        ) : editing ? (
          <MessageEditor initialContent={content} saving={savingEdit} onSubmit={onSubmitEdit} onCancel={onCancelEdit} />
        ) : (
          <>
            <div
              className={`${classes.container} ${mine ? classes.mine : classes.other} ${
                senderLeft && !mine ? classes.left : ""
              } ${highlighted ? classes.highlighted : ""}`}
            >
              {pinned && (
                <Typography className={classes.pinnedLabel}>
                  <span aria-hidden>📌</span> {t("chatMessage.pinnedLabel")}
                </Typography>
              )}

              {repliedTo && (
                <ButtonBase
                  className={`${classes.quote} ${mine ? classes.quoteMine : classes.quoteOther}`}
                  onClick={() => onJumpToMessage(repliedTo.id)}
                >
                  <Typography className={classes.quoteSender}>{repliedTo.senderName}</Typography>

                  <Typography className={classes.quoteContent}>
                    {repliedTo.deleted ? t("chatMessage.deletedMessage") : repliedTo.content || t("attachmentLabel")}
                  </Typography>
                </ButtonBase>
              )}

              {!mine && (
                <Typography className={classes.sender}>
                  {senderName || senderId}
                  {senderLeft && <span className={classes.leftLabel}> {t("chatMessage.senderLeftLabel")}</span>}
                </Typography>
              )}

              <Typography className={`${classes.text} ${emojiOnly ? classes.emojiOnly : ""}`}>
                <MentionedContent content={content} mentions={message.mentions} currentUserId={currentUserId} />
              </Typography>

              {mediaFiles.length > 0 && (
                <div className={classes.attachmentRow}>
                  {mediaFiles.map((file) => (
                    <div key={file.id} className={classes.attachmentItem}>
                      <ChatAttachment file={file} />
                    </div>
                  ))}
                </div>
              )}

              {otherFiles.length > 0 && (
                <div className={classes.attachmentRow}>
                  {otherFiles.map((file) => (
                    <div key={file.id} className={classes.attachmentItem}>
                      <ChatAttachment file={file} />
                    </div>
                  ))}
                </div>
              )}

              <Typography className={classes.time} title={createdAt.toLocaleString()}>
                {message.editedAt && <span className={classes.edited}>{t("chatMessage.editedLabel")}</span>}

                {time}

                {showReceipt && (
                  <Tooltip
                    title={readBy.length ? t("chatMessage.readByTooltip", { names: readByLabel }) : common("sent")}
                  >
                    <span className={classes.receipt}>
                      <Check size={12} />
                      {readBy.length > 0 && <Check size={12} className={classes.receiptSecondTick} />}
                    </span>
                  </Tooltip>
                )}
              </Typography>
            </div>

            {reactionGroups.length > 0 && (
              <div className={classes.reactions}>
                {reactionGroups.map((group) => (
                  <Tooltip
                    key={group.emoji}
                    title={t("chatMessage.reactionTooltip", { names: group.names.join(", "), emoji: group.emoji })}
                  >
                    <ButtonBase
                      className={`${classes.reaction} ${group.mine ? classes.reactionMine : ""}`}
                      onClick={() => onToggleReaction(group.emoji, !group.mine)}
                    >
                      <span className={classes.reactionEmoji}>{group.emoji}</span>

                      <span>{group.names.length}</span>
                    </ButtonBase>
                  </Tooltip>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {!editing && !deleted && (
        <div className={classes.actions}>
          <MessageActionsMenu
            canModify={canModify}
            pinned={pinned}
            onReply={onReply}
            onAddReaction={onAddReaction}
            onTogglePin={onTogglePin}
            onEdit={onStartEdit}
            onDelete={() => onSetDeleted(true)}
          />
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),

    "&:hover $actions, & $actions:focus-within": {
      opacity: 1,
    },
  },

  rowMine: {
    flexDirection: "row-reverse",
  },

  rowOther: {
    justifyContent: "flex-start",
  },

  stack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0,
  },

  stackMine: {
    alignItems: "flex-end",
  },

  actions: {
    display: "flex",
    flexShrink: 0,
    opacity: 0,
    transition: theme.transitions.create("opacity"),
  },

  container: {
    maxWidth: 420,
    padding: theme.spacing(1.5),
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    transition: theme.transitions.create("box-shadow"),
  },

  pinnedLabel: {
    marginBottom: theme.spacing(0.5),
    fontSize: 11,
    fontWeight: 600,
    opacity: 0.8,
  },

  deleted: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
    background: theme.palette.action.hover,
  },

  deletedText: {
    fontStyle: "italic",
    color: theme.palette.text.secondary,
  },

  undo: {
    flexShrink: 0,
  },

  highlighted: {
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
  },

  // Keeps the line breaks that were typed, and breaks a long unbroken string rather than
  // letting it widen the bubble.
  text: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },

  emojiOnly: {
    fontSize: 36,
    lineHeight: 1.2,
  },

  attachmentRow: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap", // this keeps attachments in a single row
    alignItems: "flex-start",
    gap: theme.spacing(1),
    overflowX: "auto",
  },

  attachmentItem: {
    flex: "0 1 auto",
    maxWidth: "100%",
  },

  mine: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  other: {
    background: theme.palette.grey[200],
  },

  // A former participant's message, set apart from the ones still in the conversation.
  left: {
    background: theme.palette.grey[400],
    color: theme.palette.text.secondary,
  },

  leftLabel: {
    fontWeight: 400,
    fontStyle: "italic",
    opacity: 0.8,
  },

  reactions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },

  reaction: {
    height: 24,
    padding: theme.spacing(0, 0.75),
    gap: theme.spacing(0.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  reactionMine: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },

  reactionEmoji: {
    fontSize: 14,
    lineHeight: 1,
  },

  quote: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(0.5),
    borderRadius: 6,
    borderLeft: "3px solid currentColor",
  },

  quoteMine: {
    background: "rgba(255, 255, 255, 0.18)",
  },

  quoteOther: {
    background: "rgba(0, 0, 0, 0.06)",
  },

  quoteSender: {
    fontSize: 12,
    fontWeight: 600,
  },

  quoteContent: {
    maxWidth: "100%",
    fontSize: 12,
    opacity: 0.8,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  sender: {
    fontWeight: 600,
    marginBottom: 4,
  },

  edited: {
    marginRight: theme.spacing(0.5),
    fontStyle: "italic",
  },

  time: {
    alignSelf: "flex-end",
    marginTop: 4,
    fontSize: 11,
    opacity: 0.7,
  },

  receipt: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: theme.spacing(0.5),
    verticalAlign: "middle",
  },

  // Overlaps the two ticks into the familiar double-tick glyph.
  receiptSecondTick: {
    marginLeft: -6,
  },
}))

export default ChatMessage
